import { createBaseExportSchema } from '@app/modules/social-export/components/validation/useBaseExportSchema';
import { useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';
import { z } from 'zod';

const INSTAGRAM_DESCRIPTION_MAX_LENGTH = 2200;

/** Builds the Zod schema for the Instagram publish form. */
export function createInstagramPublishSchema(t: IntlFormatters['$t']) {
  return createBaseExportSchema(t)
    .extend({
      content: z.string().trim().min(1).max(INSTAGRAM_DESCRIPTION_MAX_LENGTH),
      shareToFeed: z.boolean(),
      userTags: z.string().optional(),
      thumbType: z.enum(['DEFAULT', 'IMAGE_URL', 'VIDEO_FRAME']),
      coverUrl: z.string().nullable().optional(),
      thumbOffsetMs: z.number().nullable().optional(),
    })
    .refine(
      (data) => {
        if (data.thumbType === 'IMAGE_URL') {
          return !!data.coverUrl;
        }
        return true;
      },
      { path: ['coverUrl'] },
    )
    .refine(
      (data) => {
        if (data.thumbType === 'VIDEO_FRAME') {
          return data.thumbOffsetMs !== null && data.thumbOffsetMs !== undefined;
        }
        return true;
      },
      { path: ['thumbOffsetMs'] },
    );
}

export type InstagramPublishValues = z.infer<ReturnType<typeof createInstagramPublishSchema>>;

/** Zod schema for the Instagram publish form with translated validation messages. */
export function useInstagramPublishSchema() {
  const { $t } = useIntl();
  return useMemo(() => createInstagramPublishSchema($t), [$t]);
}
